import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Save,
  Trash2,
  ChevronUp,
  ChevronDown,
  Megaphone,
  Play,
  Pause,
  Link as LinkIcon,
  Image as ImageIcon,
  Clock,
  Share2,
  MoveRight,
} from "lucide-react";

/**
 * Ad Banner Admin – backend-like tool for creating and managing banner ads
 *
 * Features
 *  - Create/Edit/Delete ads (title, body, CTA, links, dates)
 *  - Choose 1/2/3 modules; option to merge (takeover)
 *  - Social/website links per ad
 *  - Hero-slider preview with auto-rotate + pause-on-hover
 *  - Grid preview (1/2/3 modules) incl. 3-col takeover
 *  - Reorder ads; toggle active; duplicate
 *  - Import/Export JSON for backend handoff
 *
 * No external UI deps (Tailwind CSS only)
 */

// Types
const gradients = [
  "from-amber-200 via-yellow-200 to-orange-200",
  "from-fuchsia-200 via-pink-200 to-purple-200",
  "from-indigo-200 via-sky-200 to-cyan-200",
  "from-emerald-200 via-teal-200 to-cyan-200",
  "from-rose-200 via-pink-200 to-purple-200",
];

function nowISO() {
  return new Date().toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm
}

const emptyAd = () => ({
  id: crypto.randomUUID(),
  title: "Sponset • Ny annonse",
  body: "Beskriv produkt eller kampanje kort og klart.",
  ctaText: "Les mer",
  ctaUrl: "https://",
  image:
    "https://images.unsplash.com/photo-1605727216801-e27ce1d0cc28?q=80&w=1600&auto=format&fit=crop",
  gradient: gradients[1],
  modules: 3, // 1|2|3
  takeover: false,
  active: true,
  startAt: nowISO(),
  endAt: "",
  socials: { website: "https://", instagram: "", youtube: "", tiktok: "", x: "" },
});

function isLive(ad, d = new Date()) {
  if (!ad.active) return false;
  const start = ad.startAt ? new Date(ad.startAt) : null;
  const end = ad.endAt ? new Date(ad.endAt) : null;
  if (start && d < start) return false;
  if (end && d > end) return false;
  return true;
}

// Card used in preview/grid
function AdCard({ ad }) {
  return (
    <div className={`relative overflow-hidden rounded-3xl shadow-md p-6 bg-gradient-to-r ${ad.gradient}`}>
      <div className="grid gap-4 md:grid-cols-3 items-center">
        <div className="md:col-span-2">
          <h3 className="text-2xl font-extrabold text-purple-900">{ad.title}</h3>
          <p className="mt-2 text-gray-800">{ad.body}</p>
          <div className="mt-4 flex items-center gap-2">
            <a href={ad.ctaUrl || "#"} className="rounded-full bg-purple-700 hover:bg-purple-800 text-white px-5 py-2 font-semibold">{ad.ctaText || "Les mer"}</a>
            <a href={ad.socials?.website || "#"} className="text-sm inline-flex items-center gap-1 text-purple-800 hover:underline">
              <LinkIcon className="w-4 h-4"/> Nettside
            </a>
            {ad.socials?.instagram && (
              <a href={ad.socials.instagram} className="text-sm inline-flex items-center gap-1 text-purple-800 hover:underline"><Share2 className="w-4 h-4"/> Instagram</a>
            )}
            {ad.socials?.youtube && (
              <a href={ad.socials.youtube} className="text-sm inline-flex items-center gap-1 text-purple-800 hover:underline"><Share2 className="w-4 h-4"/> YouTube</a>
            )}
          </div>
        </div>
        <div className="md:col-span-1">
          <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl border border-purple-100 bg-white/60">
            {ad.image ? (
              <img src={ad.image} alt="annonse" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-purple-400"><ImageIcon className="w-6 h-6 mr-2"/>Bildeplass</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdvertismentAdminDashboard() {
  const [ads, setAds] = useState(() => {
    // seed with 3 demo ads
    return [
      { ...emptyAd(), title: "Sponset • Annonse 1", gradient: gradients[0] },
      { ...emptyAd(), title: "Sponset • Annonse 2", gradient: gradients[1], image: "https://images.unsplash.com/photo-1620504156306-4f6e1980f360?q=80&w=1600&auto=format&fit=crop" },
      { ...emptyAd(), title: "Sponset • Annonse 3", gradient: gradients[2], image: "https://images.unsplash.com/photo-1601731816741-097824f756d9?q=80&w=1600&auto=format&fit=crop" },
    ];
  });
  const [selectedId, setSelectedId] = useState(ads[0]?.id);
  const selected = useMemo(() => ads.find((a) => a.id === selectedId), [ads, selectedId]);

  // Slider controls
  const liveAds = useMemo(() => ads.filter((a) => isLive(a)), [ads]);
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [intervalMs, setIntervalMs] = useState(5000);

  useEffect(() => {
    if (paused || liveAds.length === 0) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % liveAds.length), intervalMs);
    return () => clearInterval(id);
  }, [paused, intervalMs, liveAds.length]);

  // CRUD helpers
  function saveCurrent() {
    setAds((list) => list.map((a) => (a.id === selected.id ? { ...selected } : a)));
  }
  function addNew() {
    const n = emptyAd();
    setAds((list) => [n, ...list]);
    setSelectedId(n.id);
  }
  function duplicate(id) {
    setAds((list) => {
      const idx = list.findIndex((a) => a.id === id);
      if (idx === -1) return list;
      const copy = { ...list[idx], id: crypto.randomUUID(), title: list[idx].title + " (kopi)" };
      const nl = [...list];
      nl.splice(idx + 1, 0, copy);
      return nl;
    });
  }
  function remove(id) {
    setAds((list) => list.filter((a) => a.id !== id));
    if (id === selectedId) setSelectedId((s) => (ads.find((a) => a.id !== id)?.id));
  }
  function move(id, dir) {
    setAds((list) => {
      const i = list.findIndex((a) => a.id === id);
      if (i === -1) return list;
      const j = dir === "up" ? Math.max(0, i - 1) : Math.min(list.length - 1, i + 1);
      const nl = [...list];
      const [item] = nl.splice(i, 1);
      nl.splice(j, 0, item);
      return nl;
    });
  }

  function exportJson() {
    const payload = JSON.stringify(ads, null, 2);
    navigator.clipboard?.writeText(payload);
    alert("JSON kopiert til utklippstavlen.");
  }

  function importJson(e) {
    try {
      const parsed = JSON.parse(e.target.value);
      if (!Array.isArray(parsed)) return;
      setAds(parsed);
      setSelectedId(parsed[0]?.id);
    } catch (err) {
      // ignore
    }
  }

  return (
    <div className="mx-auto max-w-7xl p-6">
      <header className="mb-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-purple-100 text-purple-800 px-3 py-1">
          <Megaphone className="w-4 h-4"/> <span className="font-semibold">Annonse‑administrator</span>
        </div>
        <button onClick={addNew} className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-4 py-2 text-white font-semibold hover:bg-purple-700">
          <Plus className="w-4 h-4"/> Ny annonse
        </button>
        <button onClick={saveCurrent} className="inline-flex items-center gap-2 rounded-full border border-purple-300 px-4 py-2 text-purple-700 hover:bg-purple-50">
          <Save className="w-4 h-4"/> Lagre
        </button>
        <div className="ml-auto flex items-center gap-3">
          <button onClick={() => setPaused((p) => !p)} className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm">
            {paused ? <><Play className="w-4 h-4"/> Start rotasjon</> : <><Pause className="w-4 h-4"/> Pause rotasjon</>}
          </button>
          <div className="text-sm flex items-center gap-2">
            <Clock className="w-4 h-4"/> Intervall
            <select value={intervalMs} onChange={(e) => setIntervalMs(Number(e.target.value))} className="rounded-md border px-2 py-1">
              <option value={3000}>3s</option>
              <option value={5000}>5s</option>
              <option value={8000}>8s</option>
              <option value={12000}>12s</option>
            </select>
          </div>
        </div>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Left: Form */}
        <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
          {selected ? (
            <form className="grid gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium">Tittel</label>
                <input value={selected.title} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, title:e.target.value}:a))} className="rounded-xl border px-3 py-2"/>
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">Ingress</label>
                <textarea value={selected.body} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, body:e.target.value}:a))} rows={3} className="rounded-xl border px-3 py-2"/>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label className="text-sm font-medium">CTA‑tekst</label>
                  <input value={selected.ctaText} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, ctaText:e.target.value}:a))} className="rounded-xl border px-3 py-2"/>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">CTA‑lenke</label>
                  <input value={selected.ctaUrl} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, ctaUrl:e.target.value}:a))} className="rounded-xl border px-3 py-2"/>
                </div>
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">Bilde‑URL</label>
                <input value={selected.image} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, image:e.target.value}:a))} className="rounded-xl border px-3 py-2"/>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Bakgrunn</label>
                  <select value={selected.gradient} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, gradient:e.target.value}:a))} className="rounded-xl border px-3 py-2">
                    {gradients.map((g,i)=> <option key={i} value={g}>{g}</option>)}
                  </select>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Moduler</label>
                  <select value={selected.modules} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, modules:Number(e.target.value)}:a))} className="rounded-xl border px-3 py-2">
                    <option value={1}>1 modul</option>
                    <option value={2}>2 moduler</option>
                    <option value={3}>3 moduler</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input id="takeover" type="checkbox" checked={!!selected.takeover} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, takeover:e.target.checked}:a))} className="h-4 w-4"/>
                <label htmlFor="takeover" className="text-sm">Slå sammen (takeover)</label>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label className="text-sm font-medium flex items-center gap-2"><Clock className="w-4 h-4"/> Start</label>
                  <input type="datetime-local" value={selected.startAt} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, startAt:e.target.value}:a))} className="rounded-xl border px-3 py-2"/>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium flex items-center gap-2"><Clock className="w-4 h-4"/> Slutt (valgfri)</label>
                  <input type="datetime-local" value={selected.endAt} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, endAt:e.target.value}:a))} className="rounded-xl border px-3 py-2"/>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Nettside</label>
                  <input value={selected.socials?.website||""} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, socials:{...a.socials, website:e.target.value}}:a))} className="rounded-xl border px-3 py-2"/>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">Instagram</label>
                  <input value={selected.socials?.instagram||""} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, socials:{...a.socials, instagram:e.target.value}}:a))} className="rounded-xl border px-3 py-2"/>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">YouTube</label>
                  <input value={selected.socials?.youtube||""} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, socials:{...a.socials, youtube:e.target.value}}:a))} className="rounded-xl border px-3 py-2"/>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium">TikTok / X</label>
                  <input value={selected.socials?.tiktok||""} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, socials:{...a.socials, tiktok:e.target.value}}:a))} className="rounded-xl border px-3 py-2"/>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm">Aktiv</label>
                <input type="checkbox" checked={selected.active} onChange={(e)=> setAds((l)=> l.map(a=> a.id===selected.id?{...a, active:e.target.checked}:a))}/>
              </div>
            </form>
          ) : (
            <p className="text-gray-600">Ingen annonse valgt.</p>
          )}

          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={exportJson} className="rounded-full border border-purple-300 px-4 py-2 text-purple-700 hover:bg-purple-50">Eksporter JSON</button>
            <details className="w-full">
              <summary className="cursor-pointer text-sm text-gray-700">Importer JSON</summary>
              <textarea onChange={importJson} rows={6} className="mt-2 w-full rounded-xl border px-3 py-2" placeholder="Lim inn JSON‑array her..." />
            </details>
          </div>
        </div>

        {/* Right: List + reorder */}
        <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
          <h4 className="mb-3 text-sm font-semibold text-gray-700">Kø / rekkefølge (øverst = først i slider)</h4>
          <ul className="space-y-3">
            {ads.map((a, idx) => (
              <li key={a.id} className={`rounded-xl border p-3 ${selectedId===a.id? 'border-purple-600' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <button onClick={()=> move(a.id,'up')} title="Opp"><ChevronUp className="w-4 h-4"/></button>
                  <button onClick={()=> move(a.id,'down')} title="Ned"><ChevronDown className="w-4 h-4"/></button>
                  <button onClick={()=> setSelectedId(a.id)} className="flex-1 text-left">
                    <div className="font-semibold">{a.title}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2"><Clock className="w-3 h-3"/> {a.startAt || 'nå'} {a.endAt? `→ ${a.endAt}`: ''} • {a.modules} modul{a.modules>1?'er':''}{a.takeover? ' • takeover':''} • {a.active? 'Aktiv': 'Inaktiv'}</div>
                  </button>
                  <button onClick={()=> duplicate(a.id)} className="text-xs rounded-full border px-3 py-1">Kopier</button>
                  <button onClick={()=> remove(a.id)} className="text-xs rounded-full border px-3 py-1 text-red-600"><Trash2 className="w-4 h-4 inline mr-1"/>Slett</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Live slider preview */}
      <section className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2"><Megaphone className="w-5 h-5"/> Forhåndsvisning – hero‑slider</h3>
          <div className="text-sm text-gray-600">{liveAds.length} aktive annonser • bytter hver {intervalMs/1000}s</div>
        </div>
        <div className="relative rounded-3xl border border-purple-100 bg-white p-4 shadow" onMouseEnter={()=> setPaused(true)} onMouseLeave={()=> setPaused(false)}>
          {liveAds.length === 0 ? (
            <div className="p-10 text-center text-gray-500">Ingen aktive annonser i gjeldende tidsrom.</div>
          ) : (
            <AdCard ad={liveAds[slide]} />
          )}
          <div className="mt-3 flex items-center justify-center gap-2">
            {liveAds.map((_, i) => (
              <button key={i} onClick={()=> setSlide(i)} className={`h-2 w-2 rounded-full ${i===slide? 'bg-purple-700':'bg-purple-300'}`}/>
            ))}
          </div>
        </div>
      </section>

      {/* Grid preview for modules */}
      <section className="mt-8">
        <h3 className="text-lg font-bold text-purple-900 mb-3 flex items-center gap-2"><MoveRight className="w-5 h-5"/> Modul‑grid</h3>
        {selected ? (
          selected.takeover && selected.modules >= 2 ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="md:col-span-3"><AdCard ad={selected} /></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {Array.from({ length: selected.modules }).map((_,i)=> (
                <div key={i}><AdCard ad={selected} /></div>
              ))}
            </div>
          )
        ) : (
          <div className="p-6 rounded-2xl border text-gray-500">Velg en annonse for å se grid‑forhåndsvisning.</div>
        )}
      </section>
    </div>
  );
}
